(function () {
  if (customElements.get('magio-pdp-gallery-lightbox')) return;

  const ZOOM_SCALE = 2;
  const TAP_THRESHOLD = 8;
  const SLIDE_TRANSITION_MS = 350;
  const MOBILE_LIGHTBOX_MQ = window.matchMedia('(max-width: 989px)');

  const isMobileLightbox = () => MOBILE_LIGHTBOX_MQ.matches;

  customElements.define(
    'magio-pdp-gallery-lightbox',
    class MagioPdpGalleryLightbox extends HTMLElement {
      constructor() {
        super();
        this.activeIndex = 0;
        this.slides = [];
        this.thumbs = [];
        this.opener = null;

        this.onKeyUp = this.onKeyUp.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onLightboxClick = this.onLightboxClick.bind(this);
        this.onPreventScroll = this.onPreventScroll.bind(this);
        this.scrollY = 0;
        this.transitionTimer = null;
        this.pendingSlideIndex = null;
      }

      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.refreshElements();
        this.dialog = this.querySelector('.magio-pdp-lightbox__dialog');
        this.initImageZoom();

        this.querySelectorAll('[data-magio-lightbox-close]').forEach((button) => {
          button.addEventListener('click', () => this.hide());
        });

        this.addEventListener('click', this.onLightboxClick);

        if (!this.moved) {
          this.moved = true;
          document.body.appendChild(this);
        }

        document.addEventListener('click', this.onDocumentClick, true);
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick, true);
        document.removeEventListener('keyup', this.onKeyUp);
      }

      refreshElements() {
        this.slides = Array.from(this.querySelectorAll('[data-magio-lightbox-slide]'));
        this.thumbs = Array.from(this.querySelectorAll('[data-magio-lightbox-thumb]'));
        this.prevBtn = this.querySelector('[data-magio-lightbox-prev]');
        this.nextBtn = this.querySelector('[data-magio-lightbox-next]');
        this.currentEl = this.querySelector('[data-magio-lightbox-current]');
        this.stage = this.querySelector('[data-magio-lightbox-stage]');
      }

      prefersReducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      updateSlideUI(nextIndex) {
        const maxIndex = this.slides.length - 1;

        this.thumbs.forEach((thumb, i) => {
          const isActive = i === nextIndex;
          thumb.classList.toggle('is-active', isActive);
          thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
        });

        if (this.currentEl) {
          this.currentEl.textContent = String(nextIndex + 1);
        }

        if (this.prevBtn) {
          this.prevBtn.disabled = nextIndex === 0;
        }

        if (this.nextBtn) {
          this.nextBtn.disabled = nextIndex === maxIndex;
        }
      }

      setSlideInstant(nextIndex) {
        if (this.transitionTimer) {
          clearTimeout(this.transitionTimer);
          this.transitionTimer = null;
        }
        this.pendingSlideIndex = null;

        this.resetAllZoom();

        this.slides.forEach((slide, i) => {
          slide.style.transform = '';
          slide.style.transition = '';
          slide.classList.remove('is-transitioning');
          const isActive = i === nextIndex;
          slide.classList.toggle('is-active', isActive);
          if (isActive) {
            slide.removeAttribute('hidden');
          } else {
            slide.setAttribute('hidden', '');
          }
        });
      }

      finishSlideTransition(nextIndex) {
        this.transitionTimer = null;
        this.pendingSlideIndex = null;

        this.slides.forEach((slide, i) => {
          slide.style.transform = '';
          slide.style.transition = '';
          slide.classList.remove('is-transitioning');
          const isActive = i === nextIndex;
          slide.classList.toggle('is-active', isActive);
          if (isActive) {
            slide.removeAttribute('hidden');
          } else {
            slide.setAttribute('hidden', '');
          }
        });
      }

      animateSlide(prevIndex, nextIndex) {
        if (this.transitionTimer) {
          clearTimeout(this.transitionTimer);
          this.finishSlideTransition(this.pendingSlideIndex ?? this.activeIndex);
        }

        const forward = nextIndex > prevIndex;
        const outgoing = this.slides[prevIndex];
        const incoming = this.slides[nextIndex];
        if (!outgoing || !incoming) {
          this.setSlideInstant(nextIndex);
          return;
        }

        this.resetAllZoom();
        this.pendingSlideIndex = nextIndex;

        outgoing.removeAttribute('hidden');
        incoming.removeAttribute('hidden');

        outgoing.classList.add('is-transitioning');
        incoming.classList.add('is-transitioning', 'is-active');
        outgoing.classList.remove('is-active');

        [outgoing, incoming].forEach((slide) => {
          slide.style.transition = 'none';
        });

        incoming.style.transform = forward ? 'translate3d(100%, 0, 0)' : 'translate3d(-100%, 0, 0)';
        outgoing.style.transform = 'translate3d(0, 0, 0)';

        incoming.offsetHeight;

        [outgoing, incoming].forEach((slide) => {
          slide.style.transition = '';
        });

        incoming.style.transform = 'translate3d(0, 0, 0)';
        outgoing.style.transform = forward ? 'translate3d(-100%, 0, 0)' : 'translate3d(100%, 0, 0)';

        this.transitionTimer = setTimeout(() => {
          this.finishSlideTransition(nextIndex);
        }, SLIDE_TRANSITION_MS);
      }

      initImageZoom() {
        const stage = this.stage;

        this.querySelectorAll('[data-magio-lightbox-zoom-viewport]').forEach((viewport) => {
          if (viewport.dataset.magioZoomInit === 'true') return;
          viewport.dataset.magioZoomInit = 'true';

          const img = viewport.querySelector('.magio-pdp-lightbox__image');
          if (!img) return;

          const state = {
            scale: 1,
            x: 0,
            y: 0,
            pointerId: null,
            startX: 0,
            startY: 0,
            panStartX: 0,
            panStartY: 0,
            moved: false,
            lastPointerUpTime: 0,
            lastPointerUpX: 0,
            lastPointerUpY: 0,
            pendingTapTimer: null,
            panEnabled: false,
          };

          const isStageExpanded = () => stage?.classList.contains('is-expanded');

          const applyTransform = () => {
            img.style.transform = `translate(${state.x}px, ${state.y}px) scale(${state.scale})`;
            viewport.classList.toggle('is-zoomed', state.scale > 1 || isStageExpanded());
          };

          const clearPendingTap = () => {
            if (state.pendingTapTimer) {
              clearTimeout(state.pendingTapTimer);
              state.pendingTapTimer = null;
            }
          };

          const resetImageTransform = () => {
            clearPendingTap();
            state.scale = 1;
            state.x = 0;
            state.y = 0;
            state.panEnabled = false;
            viewport.classList.remove('is-dragging');
            applyTransform();
          };

          const expandStage = () => {
            stage?.classList.add('is-expanded');
            viewport.classList.add('is-zoomed');
          };

          const collapseStage = () => {
            resetImageTransform();
            stage?.classList.remove('is-expanded');
            viewport.classList.remove('is-zoomed');
          };

          viewport.resetZoom = resetImageTransform;
          viewport.collapseStage = collapseStage;

          const clampPan = () => {
            if (state.scale <= 1) {
              state.x = 0;
              state.y = 0;
              return;
            }

            const vw = viewport.clientWidth;
            const vh = viewport.clientHeight;
            const maxX = (vw * (state.scale - 1)) / 2;
            const maxY = (vh * (state.scale - 1)) / 2;
            state.x = Math.max(-maxX, Math.min(maxX, state.x));
            state.y = Math.max(-maxY, Math.min(maxY, state.y));
          };

          viewport.addEventListener('pointerdown', (event) => {
            if (event.pointerType === 'mouse' && event.button !== 0) return;

            const now = Date.now();
            const isDoubleClick =
              now - state.lastPointerUpTime < 400 &&
              Math.abs(event.clientX - state.lastPointerUpX) < 24 &&
              Math.abs(event.clientY - state.lastPointerUpY) < 24;

            state.pointerId = event.pointerId;
            state.startX = event.clientX;
            state.startY = event.clientY;
            state.panStartX = state.x;
            state.panStartY = state.y;
            state.moved = false;
            state.panEnabled = false;

            if (isStageExpanded() && isDoubleClick && !isMobileLightbox()) {
              clearPendingTap();
              state.scale = ZOOM_SCALE;
              const rect = viewport.getBoundingClientRect();
              const offsetX = event.clientX - rect.left - rect.width / 2;
              const offsetY = event.clientY - rect.top - rect.height / 2;
              state.x = -offsetX * (ZOOM_SCALE - 1);
              state.y = -offsetY * (ZOOM_SCALE - 1);
              clampPan();
              applyTransform();
              state.panEnabled = true;
              viewport.classList.add('is-dragging');
              viewport.setPointerCapture(event.pointerId);
            }
          });

          viewport.addEventListener('pointermove', (event) => {
            if (event.pointerId !== state.pointerId) return;

            const dx = event.clientX - state.startX;
            const dy = event.clientY - state.startY;

            if (Math.abs(dx) > TAP_THRESHOLD || Math.abs(dy) > TAP_THRESHOLD) {
              state.moved = true;
            }

            if (state.scale > 1 && state.panEnabled) {
              event.preventDefault();
              state.x = state.panStartX + dx;
              state.y = state.panStartY + dy;
              clampPan();
              applyTransform();
            }
          });

          viewport.addEventListener('pointerup', (event) => {
            if (event.pointerId !== state.pointerId) return;

            if (state.panEnabled) {
              state.pointerId = null;
              state.panEnabled = false;
              viewport.classList.remove('is-dragging');
              state.lastPointerUpTime = Date.now();
              state.lastPointerUpX = event.clientX;
              state.lastPointerUpY = event.clientY;
              try {
                viewport.releasePointerCapture(event.pointerId);
              } catch (e) {
                /* ignore */
              }
              return;
            }

            if (!state.moved && !isMobileLightbox()) {
              if (!isStageExpanded()) {
                clearPendingTap();
                expandStage();
              } else {
                clearPendingTap();
                state.pendingTapTimer = setTimeout(() => {
                  collapseStage();
                  state.pendingTapTimer = null;
                }, 280);
              }
            }

            state.pointerId = null;
            state.lastPointerUpTime = Date.now();
            state.lastPointerUpX = event.clientX;
            state.lastPointerUpY = event.clientY;
          });

          viewport.addEventListener('pointercancel', () => {
            clearPendingTap();
            state.pointerId = null;
            state.panEnabled = false;
            viewport.classList.remove('is-dragging');
          });
        });
      }

      resetAllZoom() {
        this.stage?.classList.remove('is-expanded');
        this.querySelectorAll('[data-magio-lightbox-zoom-viewport]').forEach((viewport) => {
          if (typeof viewport.resetZoom === 'function') {
            viewport.resetZoom();
          }
          viewport.classList.remove('is-zoomed', 'is-dragging');
        });
      }

      onLightboxClick(event) {
        if (!this.classList.contains('is-open')) return;

        const thumb = event.target.closest('[data-magio-lightbox-thumb]');
        if (thumb) {
          event.preventDefault();
          const index = parseInt(thumb.getAttribute('data-slide-index'), 10);
          if (!Number.isNaN(index)) this.goTo(index);
          return;
        }

        if (event.target.closest('[data-magio-lightbox-prev]')) {
          event.preventDefault();
          this.goTo(this.activeIndex - 1);
          return;
        }

        if (event.target.closest('[data-magio-lightbox-next]')) {
          event.preventDefault();
          this.goTo(this.activeIndex + 1);
        }
      }

      onDocumentClick(event) {
        const zoomBtn = event.target.closest('[data-magio-pdp-zoom]');
        if (!zoomBtn) return;

        const targetId = zoomBtn.getAttribute('data-lightbox');
        if (!targetId || targetId !== `#${this.id}`) return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        this.show(zoomBtn.getAttribute('data-media-id'), zoomBtn);
      }

      onKeyUp(event) {
        if (!this.classList.contains('is-open')) return;

        if (event.code === 'Escape') {
          this.hide();
        } else if (event.code === 'ArrowLeft') {
          this.goTo(this.activeIndex - 1);
        } else if (event.code === 'ArrowRight') {
          this.goTo(this.activeIndex + 1);
        }
      }

      onPreventScroll(event) {
        if (!this.classList.contains('is-open')) return;
        if (event.target.closest('.magio-pdp-lightbox__thumbs')) return;
        if (event.target.closest('.magio-pdp-lightbox__zoom-viewport.is-zoomed')) return;
        event.preventDefault();
      }

      lockScroll() {
        this.scrollY = window.scrollY;
        document.documentElement.classList.add('magio-pdp-lightbox-open');
        document.body.classList.add('magio-pdp-lightbox-open', 'overflow-hidden');
        document.body.style.top = `-${this.scrollY}px`;
        document.addEventListener('wheel', this.onPreventScroll, { passive: false });
        document.addEventListener('touchmove', this.onPreventScroll, { passive: false });
      }

      unlockScroll() {
        document.documentElement.classList.remove('magio-pdp-lightbox-open');
        document.body.classList.remove('magio-pdp-lightbox-open', 'overflow-hidden');
        document.body.style.top = '';
        document.removeEventListener('wheel', this.onPreventScroll);
        document.removeEventListener('touchmove', this.onPreventScroll);
        window.scrollTo(0, this.scrollY);
      }

      show(mediaId, opener) {
        this.refreshElements();
        this.initImageZoom();
        if (!this.slides.length) return;

        this.opener = opener || null;
        let index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
        if (index < 0) index = 0;

        this.resetAllZoom();
        this.goTo(index, { animate: false });
        this.removeAttribute('hidden');
        this.classList.add('is-open');
        this.lockScroll();

        document.addEventListener('keyup', this.onKeyUp);

        if (typeof trapFocus === 'function' && this.dialog) {
          trapFocus(this, this.dialog);
        }
      }

      hide() {
        if (!this.classList.contains('is-open')) return;

        if (this.transitionTimer) {
          clearTimeout(this.transitionTimer);
          this.transitionTimer = null;
        }
        this.pendingSlideIndex = null;

        this.resetAllZoom();
        this.classList.remove('is-open');
        this.setAttribute('hidden', '');

        this.unlockScroll();
        document.removeEventListener('keyup', this.onKeyUp);

        if (typeof removeTrapFocus === 'function') {
          removeTrapFocus(this.opener);
        }

        this.opener = null;
      }

      goTo(index, options = {}) {
        if (!this.slides.length) return;

        const maxIndex = this.slides.length - 1;
        const parsed = parseInt(index, 10);
        const nextIndex = Math.max(0, Math.min(Number.isNaN(parsed) ? 0 : parsed, maxIndex));
        const prevIndex = this.activeIndex;
        const animate = options.animate !== false && !this.prefersReducedMotion();

        if (prevIndex === nextIndex && !this.transitionTimer) return;

        this.activeIndex = nextIndex;
        this.updateSlideUI(nextIndex);

        if (!animate || prevIndex === nextIndex) {
          this.setSlideInstant(nextIndex);
          return;
        }

        this.animateSlide(prevIndex, nextIndex);
      }
    }
  );

  function initFallbackGalleryZoom() {
    document.querySelectorAll('.magio-pdp__media .product__media-toggle[data-media-id]').forEach((toggle) => {
      if (toggle.dataset.magioLightboxBound === 'true') return;
      toggle.dataset.magioLightboxBound = 'true';

      toggle.addEventListener(
        'click',
        (event) => {
          const productInfo = toggle.closest('product-info');
          const sectionId = productInfo?.dataset.section;
          if (!sectionId) return;

          const lightbox = document.querySelector(`#MagioGalleryLightbox-${sectionId}`);
          if (!lightbox || typeof lightbox.show !== 'function') return;

          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();

          lightbox.show(toggle.getAttribute('data-media-id'), toggle);
        },
        true
      );
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFallbackGalleryZoom);
  } else {
    initFallbackGalleryZoom();
  }
})();
